import React from 'react';
import { ChevronRight } from '@core/icons-react';
import { Tag, seg as tagSeg } from '../../../tags/Tag';
import type { TagProps } from '../../../tags/Tag';
import { SkeletonBar } from '../../../../skeletons';
import { BaseTile } from '../../shared';

// Left side options (4 types)
export type InfoTileLeftOption = 
  | { type: 'text'; text: string }
  | { type: 'tertiaryButton'; variant: 'icon' | 'icontext' | 'text'; icon?: React.ComponentType<any>; text?: string; onClick?: () => void; disabled?: boolean }
  | { type: 'stateTag'; props: TagProps }
  | { type: 'primaryTag'; props: TagProps };

// Right side options (3 types)
export type InfoTileRightOption = 
  | { type: 'tertiaryButton'; variant: 'icon' | 'icontext' | 'text'; icon?: React.ComponentType<any>; text?: string; onClick?: () => void; disabled?: boolean }
  | { type: 'stateTag'; props: TagProps }
  | { type: 'primaryTag'; props: TagProps };

export interface InfoTileProps {
  title: string;
  description?: string;
  
  // Footer options
  leftOption?: InfoTileLeftOption;
  rightOption?: InfoTileRightOption;
  footerMode?: boolean;
  
  // State
  disabled?: boolean;
  isLoading?: boolean;
  
  // Click handler
  onClick?: () => void;
  
  // Styling
  rounded?: boolean;
  
  // Deprecated props (for backwards compatibility)
  /** @deprecated Use leftOption instead */
  leftTag?: string;
  /** @deprecated Use rightOption instead */
  showRightButton?: boolean;
  /** @deprecated Use rightOption instead */
  rightIcon?: React.ComponentType<any>;
}

export const InfoTile: React.FC<InfoTileProps> = ({ 
  title, 
  description,
  leftOption,
  rightOption,
  footerMode = false,
  disabled = false,
  isLoading = false,
  onClick,
  rounded = false,
  
  // Deprecated props
  leftTag,
  showRightButton = false,
  rightIcon = ChevronRight
}) => {
  // Handle deprecated props
  const actualLeftOption: InfoTileLeftOption | undefined = leftOption || (leftTag ? { type: 'text', text: leftTag } : undefined);
  const actualRightOption: InfoTileRightOption | undefined = rightOption || (showRightButton ? { 
    type: 'tertiaryButton', 
    variant: 'icon',
    icon: rightIcon,
    onClick
  } : undefined);
  
  const hasFooter = actualLeftOption || actualRightOption;
  
  // Render left option
  const renderLeftOption = () => {
    if (!actualLeftOption) return null;
    
    switch (actualLeftOption.type) {
      case 'text':
        return isLoading ? (
          <SkeletonBar width="60px" height="16px" animation="pulse" />
        ) : (
          <div className="core-info-tile__footer-text">
            {actualLeftOption.text}
          </div>
        );
      
      case 'tertiaryButton':
        if (isLoading) {
          return <SkeletonBar width="32px" height="24px" animation="pulse" />;
        }
        return (
          <Tag
            template="action-sm"
            segments={[
              ...(actualLeftOption.icon ? [tagSeg.icon(actualLeftOption.icon, { size: 16 })] : []),
              ...(actualLeftOption.text ? [tagSeg.text(actualLeftOption.text, { size: 'md' })] : []),
            ]}
            disabled={actualLeftOption.disabled}
            onClick={(e) => {
              e?.stopPropagation();
              actualLeftOption.onClick?.();
            }}
          />
        );
      
      case 'stateTag':
        return isLoading ? (
          <SkeletonBar width="60px" height="24px" animation="pulse" />
        ) : (
          <Tag {...actualLeftOption.props} />
        );
      
      case 'primaryTag':
        if (isLoading) {
          return <SkeletonBar width="60px" height="24px" animation="pulse" />;
        }
        return <Tag {...actualLeftOption.props} />;
    }
  };
  
  // Render right option
  const renderRightOption = () => {
    if (!actualRightOption) return null;
    
    switch (actualRightOption.type) {
      case 'tertiaryButton':
        if (isLoading) {
          return <SkeletonBar width="32px" height="24px" animation="pulse" />;
        }
        return (
          <Tag
            template="action-sm"
            segments={[
              ...(actualRightOption.icon ? [tagSeg.icon(actualRightOption.icon, { size: 16 })] : []),
              ...(actualRightOption.text ? [tagSeg.text(actualRightOption.text, { size: 'md' })] : []),
            ]}
            disabled={actualRightOption.disabled}
            onClick={(e) => {
              e?.stopPropagation();
              actualRightOption.onClick?.();
            }}
          />
        );
      
      case 'stateTag':
        return isLoading ? (
          <SkeletonBar width="60px" height="24px" animation="pulse" />
        ) : (
          <Tag {...actualRightOption.props} />
        );
      
      case 'primaryTag':
        if (isLoading) {
          return <SkeletonBar width="32px" height="32px" animation="pulse" />;
        }
        return <Tag {...actualRightOption.props} size="lg" />;
    }
  };
  
  return (
    <BaseTile
      className={`core-info-tile ${disabled ? 'core-info-tile--disabled' : ''}`}
      onClick={disabled ? undefined : onClick}
      interactive={!disabled && !!onClick}
      rounded={rounded}
    >
      <div className="core-info-tile__content">
        <h3 className="core-info-tile__title">
          {isLoading ? (
            <SkeletonBar width="65%" height="22px" animation="pulse" />
          ) : (
            title
          )}
        </h3>
        
        {description && (
          <div className="core-info-tile__description">
            {isLoading ? (
              <>
                <SkeletonBar width="100%" height="14px" animation="pulse" />
                <SkeletonBar width="80%" height="14px" animation="pulse" style={{ marginTop: '0.25rem' }} />
              </>
            ) : (
              description
            )}
          </div>
        )}
      </div>
      
      <div className={`core-info-tile__footer ${footerMode ? 'core-info-tile__footer--mode' : ''}`}>
        {actualLeftOption && (
          <div className="core-info-tile__footer-left">
            {renderLeftOption()}
          </div>
        )}
        
        {actualRightOption && (
          <div className="core-info-tile__footer-right">
            {renderRightOption()}
          </div>
        )}
      </div>
    </BaseTile>
  );
};
